import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Task } from '../components/types';

export const LOCAL_OPERATIONAL_EVENT_NAME = 'orderly:operational-event';
export const LOCAL_OPERATIONAL_EVENT_STORAGE_KEY = 'orderly.demo.operational-events.v1';

export type LocalOperationalEventSeverity = 'info' | 'warning' | 'critical';

export interface LocalOperationalEvent {
  id: string;
  title: string;
  summary?: string;
  location?: string;
  severity: LocalOperationalEventSeverity;
  occurredAt: string;
  source?: string;
  actionHref: string;
  actionLabel?: string;
}

const MAX_STORED_EVENTS = 20;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function safeText(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, maxLength) : undefined;
}

function safeInternalHref(value: unknown): string {
  const href = safeText(value, 240);
  return href && href.startsWith('/') && !href.startsWith('//') ? href : '/tasks';
}

function createLocalEventId(): string {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function normaliseLocalOperationalEvent(detail: unknown): LocalOperationalEvent | null {
  if (!isRecord(detail)) return null;

  const title = safeText(detail.title, 160);
  if (!title) return null;

  const requestedSeverity = safeText(detail.severity, 20);
  const severity: LocalOperationalEventSeverity =
    requestedSeverity === 'critical' || requestedSeverity === 'warning'
      ? requestedSeverity
      : 'info';

  const requestedOccurredAt = safeText(detail.occurredAt, 64);
  const occurredAt = requestedOccurredAt && !Number.isNaN(Date.parse(requestedOccurredAt))
    ? new Date(requestedOccurredAt).toISOString()
    : new Date().toISOString();

  return {
    id: safeText(detail.id, 120) ?? createLocalEventId(),
    title,
    summary: safeText(detail.summary, 1000),
    location: safeText(detail.location, 160),
    severity,
    occurredAt,
    source: safeText(detail.source, 120),
    actionHref: safeInternalHref(detail.actionHref),
    actionLabel: safeText(detail.actionLabel, 80),
  };
}

function readStoredEvents(): LocalOperationalEvent[] {
  try {
    const raw = localStorage.getItem(LOCAL_OPERATIONAL_EVENT_STORAGE_KEY);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map(normaliseLocalOperationalEvent)
      .filter((event): event is LocalOperationalEvent => event !== null)
      .slice(0, MAX_STORED_EVENTS);
  } catch {
    return [];
  }
}

function storeEvents(events: LocalOperationalEvent[]): void {
  try {
    localStorage.setItem(
      LOCAL_OPERATIONAL_EVENT_STORAGE_KEY,
      JSON.stringify(events.slice(0, MAX_STORED_EVENTS)),
    );
  } catch {
    // Keep the in-memory event visible when browser storage is unavailable.
  }
}

export function toDashboardTask(event: LocalOperationalEvent, defaultActionLabel: string): Task {
  const priority = event.severity === 'critical' ? 100 : event.severity === 'warning' ? 75 : 50;

  return {
    id: `local-event-${event.id}`,
    title: event.title,
    body: event.summary,
    secondaryText: event.source,
    location: event.location,
    scheduledAt: event.occurredAt,
    priority,
    taskType: 'store',
    href: event.actionHref,
    ctaHref: event.actionHref,
    ctaText: event.actionLabel ?? defaultActionLabel,
  };
}

/**
 * Demo integration boundary for physical/local operational signals.
 *
 * A local bridge can publish an event without calling an Orderly API:
 * window.dispatchEvent(new CustomEvent('orderly:operational-event', { detail: { ... } }));
 *
 * Only display/task metadata and safe relative application routes are accepted.
 * Authentication, backend task creation and exhibition offline behaviour stay outside this boundary.
 */
export function useLocalOperationalEvents(enabled: boolean): Task[] {
  const { t } = useTranslation('drawer');
  const [events, setEvents] = useState<LocalOperationalEvent[]>([]);

  useEffect(() => {
    if (!enabled) {
      setEvents([]);
      return;
    }

    setEvents(readStoredEvents());

    const handleOperationalEvent = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const nextEvent = normaliseLocalOperationalEvent(event.detail);
      if (!nextEvent) return;

      setEvents(current => {
        const next = [
          nextEvent,
          ...current.filter(existing => existing.id !== nextEvent.id),
        ].slice(0, MAX_STORED_EVENTS);

        storeEvents(next);
        return next;
      });
    };

    window.addEventListener(LOCAL_OPERATIONAL_EVENT_NAME, handleOperationalEvent);
    return () => window.removeEventListener(LOCAL_OPERATIONAL_EVENT_NAME, handleOperationalEvent);
  }, [enabled]);

  return useMemo(
    () => events.map(event => toDashboardTask(event, t('tasks'))),
    [events, t],
  );
}
